/** @graphql agg **/
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { graphql } from 'react-relay';

import { ConfluencePageAri, ConfluenceWorkspaceAri } from '@atlassian/ari/confluence';

import { useConfluenceAnalyticsEvents } from '@confluence/analytics/entry-points/useConfluenceAnalyticsEvents.ts';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { isExperimentEnabled } from '@confluence/feature-experiments';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import { parsePagePeople } from '../utils/parsePagePeople.ts';
import { PAGE_PEOPLE_RELATIONSHIPS } from '../utils/pagePeopleRelationships.ts';
import { MAX_LISTED_PAGE_PEOPLE, rankPagePeople } from '../utils/rankPagePeople.ts';
import type { RankedPagePerson } from '../utils/rankPagePeople.ts';
import type { PageAgentContributor } from '../utils/normalizePageAgentContributors.ts';
import {
	rankPagePeopleAndAgents,
	type RankedPagePersonOrAgent,
} from '../utils/rankPagePeopleAndAgents.ts';

import type { PageAgentViewer } from './usePageAgentViewers.ts';
import type {
	usePagePeopleQuery$data,
	usePagePeopleQuery$variables,
} from './__generated__/usePagePeopleQuery.graphql.ts';
import { getPagePeopleFailedEvent, getPagePeopleSucceededEvent } from './pagePeopleAnalytics.ts';

const CANDIDATES_PER_RELATIONSHIP = 10;

const classifyError = (error: ApolloErrorInterop): 'network' | 'graphql' =>
	!error.networkError && error.graphQLErrors?.length ? 'graphql' : 'network';

const buildRelationshipBranch = (
	pageAri: string,
	{ cypherName, relationship, direction }: (typeof PAGE_PEOPLE_RELATIONSHIPS)[number],
): string => {
	const match =
		direction === 'incoming'
			? `MATCH (person)-[rel:${cypherName}]->(page {ari: "${pageAri}"})`
			: `MATCH (page {ari: "${pageAri}"})-[rel:${cypherName}]->(person)`;

	return [
		match,
		'WITH person, rel',
		'ORDER BY rel.lastUpdated DESC',
		`LIMIT ${CANDIDATES_PER_RELATIONSHIP}`,
		`RETURN person AS person, "${relationship}" AS relationship, "${direction}" AS direction, rel.lastUpdated AS relationshipTimestamp`,
	].join('\n');
};

export const buildPagePeopleCypher = (pageAri: string): string =>
	PAGE_PEOPLE_RELATIONSHIPS.map((definition) => buildRelationshipBranch(pageAri, definition)).join(
		'\nUNION ALL\n',
	);

const PagePeopleQuery = graphql`
	query usePagePeopleQuery(
		$cypherQuery: String!
		$queryContext: String!
		$version: GraphStoreCypherQueryV2VersionEnum
	) {
		graphStore_cypherQueryV2(
			query: $cypherQuery
			queryContext: $queryContext
			version: $version
			first: 110
		) @optIn(to: "GraphStoreCypherQueryV2") {
			edges {
				node {
					columns {
						key
						value {
							... on GraphStoreCypherQueryV2AriNode {
								id
								data {
									__typename
									... on AtlassianAccountUser {
										id
										accountId
										name
										picture
										accountStatus
										extendedProfile {
											jobTitle
										}
									}
									... on CustomerUser {
										id
										accountId
										name
										picture
										accountStatus
									}
								}
							}
							... on GraphStoreCypherQueryV2StringObject {
								stringValue: value
							}
							... on GraphStoreCypherQueryV2TimestampObject {
								timestampValue: value
							}
						}
					}
				}
			}
		}
	}
`;

export type UsePagePeopleOptions = {
	/** Holds discovery while the Teamwork Graph modal is closed. Defaults to true. */
	enabled?: boolean;
	agents?: readonly PageAgentViewer[];
	agentContributors?: readonly PageAgentContributor[];
};

export type UsePagePeopleResult = {
	/** Ranked and capped using the canvas's object-expansion ranking. */
	people: RankedPagePerson[];
	/** Combined, ranked section rows. The canvas continues to use `people`. */
	peopleAndAgents: RankedPagePersonOrAgent[];
	loading: boolean;
	error: ApolloErrorInterop | undefined;
};

/**
 * Discovers real people directly connected to a Confluence page through the current Teamwork Graph
 * registry snapshot, then ranks them with the page object-expansion bands so callers receive
 * canvas-ready people. This client-only request must remain gated by the modal's open state.
 */
export const usePagePeople = (
	contentId: string | undefined,
	options?: UsePagePeopleOptions,
): UsePagePeopleResult => {
	const { cloudId, activationId } = useSessionData();
	// `@confluence/analytics`, not the platform analytics-bridge helper. `fireOperationalAnalytics`
	// fires on the `analyticsBridge` channel, and `AnalyticsListener` only invokes its handler when
	// its own `channel` is `'*'` or equal to the event's; `ConfluenceAnalyticsListener` passes no
	// `channel`, so bridge events never reached it at all. See `.agents/teamwork-graph-modal.md`.
	// CCINT-9105
	const { createEvent } = useConfluenceAnalyticsEvents();
	const hasFiredRef = useRef(false);
	const skip =
		options?.enabled === false ||
		!contentId ||
		!cloudId ||
		!activationId ||
		!isExperimentEnabled('confluence_1p_and_3p_connection_byline_experiment');

	const reportError = useCallback(
		(error: ApolloErrorInterop) => {
			getMonitoringClient().submitError(error, { attribution: Attribution.INTEGRATIONS });
			markErrorAsHandled(error);
			createEvent(getPagePeopleFailedEvent({ errorType: classifyError(error) })).fire();
		},
		[createEvent],
	);

	const { data, loading, error } = useQueryInterop<
		usePagePeopleQuery$data,
		usePagePeopleQuery$variables
	>(PagePeopleQuery, {
		variables: {
			cypherQuery: skip
				? ''
				: buildPagePeopleCypher(
						ConfluencePageAri.create({ siteId: cloudId, pageId: contentId }).toString(),
					),
			queryContext: skip
				? ''
				: ConfluenceWorkspaceAri.create({ siteId: cloudId, activationId }).toString(),
			version: 'V3',
		},
		skip,
		interopApolloAggClient: true,
		errorPolicy: 'all',
		onError: reportError,
	});

	const candidates = useMemo(() => (skip ? [] : parsePagePeople(data)), [data, skip]);
	// Section role priorities must not change the canvas's people, order, or primary relationships.
	const people = useMemo(() => rankPagePeople(candidates), [candidates]);
	const ranked = useMemo(
		() =>
			rankPagePeopleAndAgents(candidates, options?.agents ?? [], options?.agentContributors ?? []),
		[candidates, options?.agents, options?.agentContributors],
	);
	const peopleAndAgents = useMemo(
		() =>
			ranked.filter(({ agent }) => agent || (!loading && !error)).slice(0, MAX_LISTED_PAGE_PEOPLE),
		[ranked, loading, error],
	);

	// Once per hook instance, so a re-render or a cache update does not inflate the
	// success count. `count` is the ranked list, so at most `MAX_LISTED_PAGE_PEOPLE` — not
	// however many a given surface goes on to show. No name, account id or other person
	// detail goes into the payload.
	useEffect(() => {
		if (skip || loading || error || hasFiredRef.current) {
			return;
		}

		hasFiredRef.current = true;
		createEvent(getPagePeopleSucceededEvent({ count: people.length })).fire();
	}, [skip, loading, error, people.length, createEvent]);

	return { people, peopleAndAgents, loading: !skip && loading, error };
};
